import { getTtsProperties, useGetTtsData } from '@/hooks/useGetTtsData';
import { Page } from '@/pages/AdvancedEditor/AdvancedEditor';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { getPageProperty, PolotnoStoreActivePageKeys, setPageProperty } from '@/pages/AdvancedEditor/hooks/polotno-store';
import { useUploadTtsCache } from '@/pages/AdvancedEditor/hooks/useUploadTtsCache';
import useTtsData from '@/services/tts/tokenManager';
import { AudioError, ttsGenerationErrors } from '@/state/presenters';
import { ScriptType } from '@d-id/types';
import { useCallback } from 'react';

export const setPageDuration = (page: Page, durationInMs: number) => {
    return page.set({
        duration: durationInMs,
        custom: {
            ...page.custom,
            [PolotnoStoreActivePageKeys.sceneDurationCalculated]: true,
        },
    });
};

export const useUpdateDuration = () => {
    const { store } = useAdvancedEditorContext();
    const { data: ttsData } = useTtsData();
    const { getTtsData } = useGetTtsData();
    const uploadTtsCache = useUploadTtsCache();

    // Update the duration of the scenes that have text as the script and update the cache if needed
    const updateDuration = useCallback(async () => {
        await Promise.all(
            store.pages
                .filter(page => !getPageProperty(page, PolotnoStoreActivePageKeys.sceneDurationCalculated))
                .map(async page => {
                    const selectedScript = getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript);
                    if (selectedScript === ScriptType.text) {
                        const setTtsType = setPageProperty(page, PolotnoStoreActivePageKeys.sceneTtsType);

                        const voice = getPageProperty(page, PolotnoStoreActivePageKeys.ttsSelectedVoice);
                        const text = getPageProperty(page, PolotnoStoreActivePageKeys.textInput);
                        const voiceConfig = getPageProperty(page, PolotnoStoreActivePageKeys.ttsVoiceConfig);

                        const { type, cacheable } = getTtsProperties(voice, text);

                        const setInlineTtsError = (ttsError: AudioError) => {
                            setPageProperty(page, PolotnoStoreActivePageKeys.ttsError)(ttsGenerationErrors[ttsError]);
                        };

                        const { duration, data, wordLevelTiming } = await getTtsData(setInlineTtsError, voice, text, voiceConfig);

                        if (cacheable) {
                            await uploadTtsCache(page, data, text, duration, wordLevelTiming);
                        }

                        setTtsType(type);
                        setPageDuration(page, duration * 1000);
                    } else {
                        const uploadedAudio = getPageProperty(page, PolotnoStoreActivePageKeys.uploadedAudio);
                        if (uploadedAudio) {
                            setPageDuration(page, uploadedAudio.duration);
                        }
                    }
                })
        );
    }, [store.pages, ttsData]);

    const durationUpdated = store.pages.every(page => !!getPageProperty(page, PolotnoStoreActivePageKeys.sceneDurationCalculated));

    const scenesDuration = store.pages.map(page => {
        const durationInSeconds = Number((page.duration / 1000).toFixed(2));
        return durationInSeconds;
    });

    const videoDuration = scenesDuration.reduce((acc, duration) => acc + duration, 0);

    return { updateDuration, durationUpdated, scenesDuration, videoDuration };
};
